import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi } from '../../context';
import { Failure } from '../../bits';
import { useT } from '../../../messages';
import type { IntakeDraft, PageTemplate } from '../../../wire';
import { SiteDialog } from './SiteDialog';

/**
 * **"Start from their answers"** — the intake → page hand-off (D5), as a
 * button and one small dialog asking which template's shape to pour the
 * answers into.
 *
 * It opens no new door. `GET …/intake/draft` is a read; the document it
 * answers is handed to the page editor exactly as picking a template is, and
 * the caller then opens the editor on it. Nothing is written and nothing is
 * published until the operator presses Publish there, which the dialog says.
 *
 * On the page card and on the review screen, which is why it is its own file.
 */
export function StartFromAnswers({
  tenantId,
  template: answeredFor,
  templates,
  primary = false,
  onStartPage,
}: {
  tenantId: string;
  /** The template the form was made for — the default choice. */
  template: string;
  templates: readonly PageTemplate[];
  primary?: boolean;
  onStartPage: (draft: IntakeDraft) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState(answeredFor);

  const start = async (): Promise<void> => {
    const ok = await action.run(async () => {
      onStartPage(await api.intakeDraft(tenantId, template));
    });
    if (ok) setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`button button--sm${primary ? ' button--primary' : ''}`}
        onClick={() => {
          action.clear();
          setTemplate(answeredFor);
          setOpen(true);
        }}
      >
        {t('site.start.button')}
      </button>
      {open ? (
        <SiteDialog title={t('site.start.title')} busy={action.busy} onClose={() => setOpen(false)}>
          <p className="modal__consequence">{t('site.start.note')}</p>
          <label className="field field--wide">
            <span className="field__label">{t('site.start.template')}</span>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              aria-label={t('site.start.template')}
            >
              {templates.some((one) => one.name === template) ? null : (
                <option value={template}>{template}</option>
              )}
              {templates.map((one) => (
                <option key={one.name} value={one.name}>
                  {one.label} · {one.vertical}
                </option>
              ))}
            </select>
          </label>
          {action.failure !== null ? <Failure failure={action.failure} /> : null}
          <div className="modal__actions">
            <button
              type="button"
              className="button"
              disabled={action.busy}
              onClick={() => setOpen(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={action.busy}
              onClick={() => void start()}
            >
              {action.busy ? t('common.working') : t('site.start.go')}
            </button>
          </div>
        </SiteDialog>
      ) : null}
    </>
  );
}
